import React, { useState, useEffect, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./UserManagement.css";
import UserCreatePopup from "./Popup/UserCreatePopup";
import UserModifyPopup from "./Popup/UserModifyPopup";
import * as userFunctions from "../../../functions/Functions";
import { formatAccess, formatAccessWbar, formatDate } from "../../../components/Util/DateFormat";
import Swal from "sweetalert2";
import { getServerBaseUrl } from "../../../utils/portConfig";
import axios from "axios";
import { getLastOAuthProviderIcon, getOAuthIcon } from "./getOAuthIcon";

// 환자 관리..
function UserManagement() {
  // State 변수들
  const [keyword, setKeyword] = useState("");
  const [selectedUser, setSelectedUser] = useState(null); // 선택된 사용자
  const [isPopupOpen, setIsPopupOpen] = useState(false); // 팝업 열림 상태
  const [popupType, setPopupType] = useState(null); // 팝업 종류

  // 테이블 높이 설정값
  const [gridApi, setGridApi] = useState(null);
  const [pageSize, setPageSize] = useState();
  const gridContainerRef = useRef(null);

  // 그리드 참조 변수
  const gridRef = useRef(null);

  const API_SERVER_V2_URL = `${getServerBaseUrl()}/api/v2/users`; // API 서버 URL
  const API_SERVER_URL = `${getServerBaseUrl()}/api/v1/users`;
  const token = localStorage.getItem("token");

  const resizeTimeoutRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        calculatePageSize();
      }, 300);
    };

    window.addEventListener("resize", handleResize);

    // 최초 1회 실행
    calculatePageSize();

    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [gridContainerRef]);

  useEffect(() => {
    if (gridApi && pageSize) {
      const dataSource = {
        getRows: async (params) => {
          await getData(params);
        },
      };
      gridApi.setDatasource(dataSource);
    }
  }, [gridApi, pageSize]);

  const getData = async (params) => {
    const page = Math.floor(params.startRow / pageSize);
    try {
      Swal.fire({
        title: "데이터를 가져오는 중...",
        text: "잠시만 기다려 주세요.",
        didOpen: () => {
          Swal.showLoading();
        },
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
      });

      const response = await axios.post(
        `${API_SERVER_V2_URL}/list`,
        {
          page: page, // 0부터 시작
          size: pageSize,
          keyword: keyword || null,
          sort: {
            createdDt: "desc",
          },
          // filters: {
          //   category: selectedCategory || null,
          // },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = response.data.data.content;
      const count = response.data.data.count;
      // const lastRow = count < params.endRow ? count : -1;
      params.successCallback(data, count);
      if (data.length === 0) {
        gridApi.showNoRowsOverlay();
      } else {
        gridApi.hideOverlay();
      }
    } catch (error) {
      console.error("Data fetch error:", error);
      Swal.fire({
        title: "오류!",
        text: "데이터를 가져오는 중 오류가 발생했습니다.",
        icon: "error",
        showConfirmButton: true,
      });
      params.successCallback([], 0);
    } finally {
      Swal.close();
    }
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      cellStyle: { textAlign: "center" },
      flex: 0.5,
    },
    {
      field: "username",
      headerName: "사용자명",
      cellStyle: { textAlign: "left" },
      flex: 0.8,
    },
    {
      field: "nickname",
      headerName: "닉네임",
      cellStyle: { textAlign: "left" },
      flex: 0.8,
    },
    {
      field: "telNumber",
      headerName: "휴대폰",
      cellStyle: { textAlign: "left" },
      flex: 0.8,
    },
    {
      field: "birthDate",
      headerName: "생년월일",
      cellStyle: { textAlign: "left" },
      flex: 0.8,
    },
    {
      field: "gender",
      headerName: "성별",
      cellStyle: { textAlign: "center" },
      valueGetter: (params) => {
        switch (params.data?.gender) {
          case "M":
            return "남성";
          case "F":
            return "여성";
          default:
            return "-";
        }
      },
      flex: 0.5,
    },
    {
      field: "patientList",
      headerName: "연동 병원 수",
      cellRenderer: (params) => {
        return params.data?.patientList ? params.data.patientList.length : 0;
      },
      flex: 0.5,
    },
    {
      field: "lastLoginAt",
      headerName: "최근접속일(수단)",
      cellRenderer: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {formatAccessWbar(params.data?.lastLoginAt)}
          {getLastOAuthProviderIcon(params.data?.oauthList)}
        </div>
      ),
      flex: 1,
    },
    {
      field: "createdAt",
      headerName: "계정생성일",
      valueFormatter: (params) => formatAccessWbar(params.data?.createdAt),
      flex: 1,
    },
    {
      field: "id",
      hide: true, // 숨겨진 필드
    },
    {
      field: "hospitalId",
      hide: true, // 숨겨진 필드
    },
  ];

  // 그리드 행 클릭 시 사용자 정보 가져오기
  const onCellClicked = (event) => {
    setSelectedUser(event.data);
    userFunctions.OpenModifyPopup(setIsPopupOpen, setPopupType);
  };

  // 팝업 닫기 함수
  const ClosePopup = () => {
    userFunctions.ClosePopup(setIsPopupOpen);
    if (gridRef.current) {
      gridRef.current.api.deselectAll();
      setSelectedUser(null);
    }
  };

  // 사용자 생성 함수
  const createUser = async (userData) => {
    await userFunctions.createUser(API_SERVER_URL, token, userData, setIsPopupOpen, refreshGrid);
  };

  // 사용자 수정 함수
  const editUser = async (editUserData) => {
    try {
      await userFunctions.editUserV2(API_SERVER_V2_URL, token, editUserData, setIsPopupOpen, refreshGrid, setSelectedUser);
    } catch (error) {
      console.error("User edit error:", error);
    } finally {
    }
  };

  // 사용자 삭제 함수
  const deleteUser = async (userId) => {
    await userFunctions.deleteUserV2(API_SERVER_V2_URL, userId, token, setIsPopupOpen, refreshGrid, setSelectedUser);
  };

  const deleteOAuthProvider = async (userId, oauthId) => {
    await userFunctions.deleteOAuthProvider(API_SERVER_V2_URL, userId, oauthId, token, refreshGrid, setSelectedUser);
  };

  // 화면 크기가 변경될 때 테이블 높이를 조정하는 함수
  const calculatePageSize = () => {
    if (gridContainerRef.current) {
      const gridHeight = gridContainerRef.current.clientHeight;
      const rowHeight = 43; // AG Grid의 기본 행 높이 (alpine 테마)
      const headerHeight = 45; // 헤더 높이
      const paginationHeight = 45; // 페이지네이션 아래 여백

      const availableRows = Math.floor((gridHeight - headerHeight - paginationHeight) / rowHeight);

      // 최소 5개, 계산된 값보다 작은 행 수로 설정
      const newPageSize = Math.max(5, availableRows);
      setPageSize(newPageSize);
    }
  };

  // 💡 AG Grid 옵션에 cacheBlockSize를 추가
  const gridOptions = useMemo(
    () => ({
      // cacheBlockSize: pageSize,
      maxBlocksInCache: 1, // 필요에 따라 설정, 기본값 1
      // ... 다른 옵션들을 여기에 추가 가능
    }),
    [pageSize]
  );

  const prevPageRef = useRef(0);

  const onPaginationChanged = () => {
    if (gridRef.current && gridRef.current.api) {
      const currentPage = gridRef.current.api.paginationGetCurrentPage();
      if (prevPageRef.current !== currentPage) {
        prevPageRef.current = currentPage;
      }
    }
  };

  // 그리드 옵션
  const defaultColDef = useMemo(() => {
    return {
      sortable: false,
      filter: false,
      resizable: true,
    };
  }, []);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const refreshGrid = () => {
    if (gridApi) {
      gridApi.refreshInfiniteCache();
    }
  };

  // Enter 키로 검색
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      keywordRefreshData();
    }
  };

  const keywordRefreshData = async () => {
    if (gridApi) {
      gridApi.setDatasource({
        getRows: async (params) => {
          await getData(params);
        },
      });
    }
  };

  return (
    <div className="dashboard-content">
      <div className="dashboard-content-container">
        <div className="dashboard-content-header">
          <h2>사용자 관리</h2>
        </div>
        <div className={"search-field-container"}>
          {/* <div className={"search-field"}>
            <label>분류</label>
            <select className={"dashboard-content-select"} value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
              <option value="">분류 선택</option>
            {categories.map((category, index) => (
                <option key={index} value={category.key}>
                  {category.name}
                </option>
              ))} 
            </select>
          </div> */}
          <div className={"search-field"}>
            <label>키워드</label>
            <input className={"keyword-field"} type="text" placeholder="키워드 입력" value={keyword} onChange={(e) => setKeyword(e.target.value)} onKeyPress={handleKeyPress} />
            <button className={"green-button"} onClick={keywordRefreshData}>
              검색
            </button>
          </div>
          {/* <div className="search-container">
            <div className="button-group">
              <button className="dashboard-content-button blue-button" onClick={OpenCreatePopup}>
                생성
              </button>
            </div>
          </div> */}
        </div>

        <UserCreatePopup isOpen={isPopupOpen && popupType === "create"} onClose={ClosePopup} onSave={createUser} />

        <UserModifyPopup
          isOpen={isPopupOpen && popupType === "modify"}
          onClose={ClosePopup}
          onSave={editUser}
          selectedUser={selectedUser}
          onDelete={deleteUser}
          deleteOAuthProvider={deleteOAuthProvider}
        />

        <div ref={gridContainerRef} className="ag-theme-alpine" style={{ height: "100%", width: "100%", fontWeight: "400" }}>
          {pageSize && (
            <AgGridReact
              ref={gridRef}
              pagination={true}
              rowModelType={"infinite"}
              onGridReady={onGridReady}
              onCellClicked={onCellClicked}
              columnDefs={columns}
              cacheBlockSize={pageSize}
              paginationPageSize={pageSize}
              defaultColDef={defaultColDef}
              gridOptions={gridOptions}
              onPaginationChanged={onPaginationChanged}
              overlayNoRowsTemplate="<span class='ag-overlay-no-rows-center'>조회된 데이터가 없습니다.</span>"
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default UserManagement;
